import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge } from '@/components/UI';
import TradeLogicExecutionJournalTrace, {
    type TradeLogicExecutionJournalRecord
} from '@/components/TradeLogic/TradeLogicExecutionJournalTrace.tsx';

const getPhaseTone = (phase: string): 'info' | 'success' => {
    if (phase === 'arbitration') {
        return 'info';
    }

    return 'success';
};

const getStatusTone = (status: string): 'warning' | 'neutral' | 'success' | 'danger' => {
    if (status === 'ready') {
        return 'warning';
    }

    if (status === 'suppressed') {
        return 'neutral';
    }

    if (status === 'executed') {
        return 'success';
    }

    return 'danger';
};

const buildRealTimeEntriesHref = (journal: TradeLogicExecutionJournalRecord): string => {
    const searchParams = new URLSearchParams();

    if (journal.row.strategyId !== null) {
        searchParams.set('strategyId', journal.row.strategyId.toString());
    }

    if (journal.row.entityId !== null) {
        searchParams.set('entityId', journal.row.entityId.toString());
    }

    if (journal.row.entityType !== null) {
        searchParams.set('entityType', journal.row.entityType);
    }

    const queryString = searchParams.toString();

    if (!queryString) {
        return '/user/realTimeEntries';
    }

    return `/user/realTimeEntries?${queryString}`;
};

const buildEntryLogHref = (entryLogId: number): string => {
    return `/user/entryLog/?entryLogId=${entryLogId}`;
};

const buildTradeLogicExecutionJournalHref = (journalId: number): string => {
    return `/user/tradeLogicExecutionJournal/${journalId}`;
};

const buildJournalLinkNodes = (journal: TradeLogicExecutionJournalRecord): React.ReactNode[] => {
    const links: React.ReactNode[] = [];

    if (journal.row.tradeId !== null) {
        links.push(
            <Link
                className="inline-flex items-center rounded-md border border-sky-800/80 bg-sky-950/60 px-2 py-1 text-[11px] font-medium text-sky-200 hover:border-sky-700 hover:text-sky-100"
                key="trade"
                to={`/user/visualizeTrade/${journal.row.tradeId}`}
            >
                Open trade
            </Link>
        );
    }

    if (journal.row.realTimeStrategyEntryId !== null) {
        if (journal.row.entryLogId !== null) {
            links.push(
                <Link
                    className="inline-flex items-center rounded-md border border-emerald-800/80 bg-emerald-950/60 px-2 py-1 text-[11px] font-medium text-emerald-200 hover:border-emerald-700 hover:text-emerald-100"
                    key="entry-log"
                    to={buildEntryLogHref(journal.row.entryLogId)}
                >
                    Open entry log
                </Link>
            );
        } else {
            links.push(
                <Link
                    className="inline-flex items-center rounded-md border border-zinc-700 bg-zinc-950/80 px-2 py-1 text-[11px] font-medium text-zinc-200 hover:border-zinc-500 hover:text-white"
                    key="entries"
                    to={buildRealTimeEntriesHref(journal)}
                >
                    Real-time entries list
                </Link>
            );
        }
    }

    links.push(
        <Link
            className="inline-flex items-center rounded-md border border-violet-800/80 bg-violet-950/60 px-2 py-1 text-[11px] font-medium text-violet-200 hover:border-violet-700 hover:text-violet-100"
            key="trace-page"
            to={buildTradeLogicExecutionJournalHref(journal.row.id)}
        >
            Open trace page
        </Link>
    );

    return links;
};

interface TradeLogicExecutionJournalCardProps {
    journal: TradeLogicExecutionJournalRecord;
    contextMeta?: React.ReactNode;
}

const TradeLogicExecutionJournalCard: React.FC<TradeLogicExecutionJournalCardProps> = ({ journal, contextMeta }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const journalLinks = buildJournalLinkNodes(journal);
    const toggleLabel = isExpanded ? 'Hide inline trace' : 'View inline trace';

    return (
        <div className="rounded-md bg-zinc-900/80 p-3">
            <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={journal.row.phase} tone={getPhaseTone(journal.row.phase)}>
                    {journal.row.phase}
                </StatusBadge>
                <StatusBadge status={journal.row.status} tone={getStatusTone(journal.row.status)}>
                    {journal.row.status}
                </StatusBadge>
                <span className="text-xs font-mono text-zinc-300">{journal.row.actionKind}</span>
                <span className="ml-auto text-xs text-zinc-500">{journal.row.createdAt}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-400">
                {contextMeta}
                <span>
                    scope: <span className="font-mono text-zinc-300">{journal.row.scopeKey}</span>
                </span>
                {journal.row.candidateId && (
                    <span>
                        candidate: <span className="font-mono text-zinc-300">{journal.row.candidateId}</span>
                    </span>
                )}
                {journal.row.tradeId !== null && <span>trade #{journal.row.tradeId}</span>}
                {journal.row.realTimeStrategyEntryId !== null && <span>entry #{journal.row.realTimeStrategyEntryId}</span>}
                {journal.row.entityId !== null && journal.row.entityType !== null && (
                    <span>
                        entity: {journal.row.entityType} #{journal.row.entityId}
                    </span>
                )}
                {journal.row.entryLogId !== null && <span>entry log #{journal.row.entryLogId}</span>}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
                {journalLinks}
                {journal.row.realTimeStrategyEntryId !== null && journal.row.tradeId === null && journal.row.entryLogId === null && (
                    <span className="text-[11px] text-zinc-500">
                        Entry journals link to the closest filtered list view available today.
                    </span>
                )}
                <button
                    aria-expanded={isExpanded}
                    className="inline-flex items-center rounded-md border border-zinc-700 bg-zinc-950/80 px-2 py-1 text-[11px] font-medium text-zinc-200 hover:border-zinc-500 hover:text-white"
                    type="button"
                    onClick={() => setIsExpanded((currentValue) => !currentValue)}
                >
                    {toggleLabel}
                </button>
            </div>
            {isExpanded && (
                <div className="mt-3 border-t border-zinc-800 pt-3">
                    <TradeLogicExecutionJournalTrace journal={journal} />
                </div>
            )}
        </div>
    );
};

export default TradeLogicExecutionJournalCard;
